import { useId, useState } from 'react'
import { useSelector } from 'react-redux'

import SettingItem from './SettingItem'
import { type BooleanSettingConfig } from './utils'

import styles from './styles.module.scss'

import InfoIcon from '@/assets/icons/info.svg?react'
import { Dropdown, type DropdownItem } from '@/components/Dropdown'
import Tooltip from '@/components/Tooltip'
import { selectPlanType } from '@/store/features/user/userSlice'
import { type EnterpriseSettings, PlanType, SettingType } from '@/types'
import { type ConnectorAccess, type ConnectorAccessKey } from '@/utils/connectorAccess'

const MCP_TOOLTIP =
  'Enable members to connect Wispr’s MCP server to other tools such as Claude and ChatGPT. This allows members to ask other AI tools about their Wispr Notetaker meeting notes and transcripts.'
const CONNECTORS_TOOLTIP = 'Which apps members are allowed to connect to the Notetaker for meeting prep and context.'

const MCP_ACCESS: BooleanSettingConfig = {
  label: 'MCP access',
  key: SettingType.mcpEnabled,
  enterpriseOnly: true,
}

const CONNECTORS: Array<BooleanSettingConfig & { accessKey: ConnectorAccessKey }> = [
  {
    label: 'Google Calendar',
    accessKey: 'google_calendar',
    key: SettingType.connectorGoogleCalendarEnabled,
    enterpriseOnly: true,
  },
  {
    label: 'Outlook Calendar',
    accessKey: 'outlook_calendar',
    key: SettingType.connectorOutlookEnabled,
    enterpriseOnly: true,
  },
  {
    label: 'Gmail',
    accessKey: 'gmail',
    key: SettingType.connectorGmailEnabled,
    enterpriseOnly: true,
  },
  {
    label: 'Slack',
    accessKey: 'slack',
    key: SettingType.connectorSlackEnabled,
    enterpriseOnly: true,
  },
  {
    label: 'Notion',
    accessKey: 'notion',
    key: SettingType.connectorNotionEnabled,
    enterpriseOnly: true,
  },
]

interface ConnectorAccessSectionProps {
  settings: EnterpriseSettings
  isAdmin: boolean
  connectorAccess: ConnectorAccess
  applySettingsPatch: (patch: Partial<EnterpriseSettings>) => Promise<void>
}

const ConnectorAccessSection = ({
  settings,
  isAdmin,
  connectorAccess,
  applySettingsPatch,
}: ConnectorAccessSectionProps) => {
  const [pendingKeys, setPendingKeys] = useState<Set<keyof EnterpriseSettings>>(() => new Set())
  const planType = useSelector(selectPlanType)
  const tooltipId = useId()
  const enterpriseLocked = planType !== PlanType.Enterprise
  const mcpEnabled = settings[SettingType.mcpEnabled] !== false
  const visibleConnectors = CONNECTORS.filter(({ accessKey }) => connectorAccess[accessKey])
  const enabledCount = visibleConnectors.filter(({ key }) => settings[key] !== false).length
  const summary = mcpEnabled ? `${enabledCount} of ${visibleConnectors.length} enabled` : 'MCP off'
  const rowsReadOnly = !isAdmin || !mcpEnabled || enterpriseLocked

  const items: DropdownItem[] = visibleConnectors.map((connector) => ({
    id: connector.key,
    title: connector.label,
    selected: settings[connector.key] !== false,
    disabled: rowsReadOnly || pendingKeys.has(connector.key),
  }))

  const toggleSetting = async (key: keyof EnterpriseSettings, checked: boolean) => {
    setPendingKeys((current) => new Set(current).add(key))
    try {
      await applySettingsPatch({ [key]: checked })
    } finally {
      setPendingKeys((current) => {
        const next = new Set(current)
        next.delete(key)
        return next
      })
    }
  }

  const handleSelect = (selected: DropdownItem[]) => {
    const changed = selected.find((item, index) => item.selected !== items[index]?.selected)
    if (!changed) return
    void toggleSetting(changed.id as keyof EnterpriseSettings, changed.selected)
  }

  const permissionCopy = !isAdmin
    ? 'Only admins can change MCP and connector access.'
    : !mcpEnabled
      ? 'Turn on MCP access to change connector access.'
      : null

  return (
    <div className={styles.connectorAccess} data-testid="mcp-connector-access-section">
      <SettingItem
        config={MCP_ACCESS}
        optimisticSettings={settings}
        toggleSettings={toggleSetting}
        defaultValue={true}
        labelTooltip={MCP_TOOLTIP}
        disabled={pendingKeys.has(SettingType.mcpEnabled)}
        isAdmin={isAdmin}
      />
      <div className={styles.settingsItem}>
        <div className={styles.toggleLabel}>
          Connectors
          <Tooltip
            text={
              <span id={tooltipId} role="tooltip">
                {CONNECTORS_TOOLTIP}
              </span>
            }
            placement="top"
            hideArrow
          >
            <span
              className={styles.infoTooltipTrigger}
              role="img"
              aria-label="About Connectors"
              aria-describedby={tooltipId}
            >
              <InfoIcon className={styles.infoIcon} aria-hidden="true" />
            </span>
          </Tooltip>
        </div>
        <Dropdown
          items={items}
          onSelect={handleSelect}
          multiDropdown
          summary={summary}
          ariaLabel={`Connectors, ${summary}`}
          className={styles.dataControlDropdown}
          dropdownChildren={
            permissionCopy ? <div className={styles.connectorPermissionCopy}>{permissionCopy}</div> : undefined
          }
        />
      </div>
    </div>
  )
}

export default ConnectorAccessSection
